import Empty from "assets/icons/Empty";
import { payerTypeConstants } from "constants/payer";
import { UserRole } from "constants/userRole";
import { useOptionalUserWithAccount } from "context/UserAccountProvider";
import { parsePrice } from "helpers/price";
import { InputText } from "primereact/inputtext";
import { TabView, TabPanel } from "primereact/tabview";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { convertToPercent } from "../../../helpers";
import { nonCoveredQualifiers } from "../../../nonCoveredQualifiers";
import { CoverageDetailsLine } from "../../CoverageDetailsLine";
import { ValueNoteSection } from "../../ValueNoteSection";
import {
  buildAiTagIndex,
  lineKey,
  pointerKey,
  summaryKey,
  tagOptionItems,
  tagSummaryItems,
} from "./aiRecommendationTags";
import {
  apiDisplayBlocks,
  BENEFIT_BLOCK_ORDER,
  blockKeyForEngineSource,
  flatCopaySourceBlock,
  PHYSICIAN_BENEFIT_BLOCKS,
  sourceBenefitBlocks,
  visibleBenefitBlocks,
} from "./coinsuranceCopayBlocks";
import { selectedCopayRow, tagSelectedCopay } from "./engineCopayListPick";
import { Message } from "./Message";

const parseLimitAmount = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === "") return null;
  const parsed = Number(String(raw).replace(/[$,%]/g, ""));
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Ani's rule: a plan total (calendar year) is the limit a remaining row counts down from, not a
 * row of its own. Adds "of $5,000.00 · $743.74 met" under each remaining row when it can be
 * worked out. A $0 or missing total means no limit; a remaining above the total is bad data; a
 * service-specific replacement deductible (`ServiceType`, CLE-2654) has its own limit — no line.
 */
const withLimit = <T extends Record<string, any>>(
  items: T[],
  limitRaw: any
): (T & { limit?: string })[] => {
  const limit = parseLimitAmount(limitRaw?.Value ?? limitRaw);
  return items.map((item) => {
    if (item.ServiceType) return item;
    const amount = parseLimitAmount(item.Value);
    if (limit === null || limit <= 0 || amount === null || amount > limit) {
      return item;
    }
    return {
      ...item,
      limit: `of ${parsePrice(limit)} · ${parsePrice(limit - amount)} met`,
    };
  });
};

/**
 * CLE-2827 — the payer sent the plan total but no remaining amount: one non-selectable "N/A" row
 * keeps the total visible ("of $5,000.00 · not reported"). No row when there is no total either.
 */
const missingRow = (name: string, limitRaw: any) => {
  const limit = parseLimitAmount(limitRaw?.Value ?? limitRaw);
  if (limit === null || limit <= 0) return [];
  return [
    { Value: "N/A", name, limit: `of ${parsePrice(limit)} · not reported` },
  ];
};

const StyledBenefitCointainer = styled.div<{
  showAdditionalDetailsSection: boolean;
  $fullWidth?: boolean;
}>`
  border-radius: 9px;
  min-height: ${({ $fullWidth }) => ($fullWidth ? "auto" : "556px")} !important;
  padding: ${({ $fullWidth }) => ($fullWidth ? "0" : "8px")};
  padding-top: 0px;
  justify-content: ${({ showAdditionalDetailsSection }) =>
    showAdditionalDetailsSection ? "flex-start" : "center"};
  display: flex;
  flex-direction: column;
  /* see min-w-0 on the root div */
  min-width: 0;
`;

const StyledTabView = styled(TabView)`
  width: 100%;
  /* see min-w-0 on the root div */
  min-width: 0;
  .p-tabview-panels {
    border: 1px solid #e2e2e4;
    margin-top: 20px;
    border-radius: 12px;
    padding-top: 0px;
    padding-left: 0px;
    padding-right: 0px;
  }
  .p-tabview-selected .p-tabview-nav-link {
    background-color: #edf1fd !important;
  }
  .p-tabview-nav {
    overflow-x: auto !important;
    overflow-y: hidden;
    white-space: nowrap !important;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  .p-tabview-nav::-webkit-scrollbar {
    height: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  .p-tabview-nav ul {
    flex-wrap: nowrap !important;
  }
`;

const StyledTabPanelheader = styled.div`
  background-color: #f2f4f7;
  color: #344054;
  padding: 12px 24px;
  border-radius: 12px 12px 0px 0px;
  border-bottom: 1px solid #eaecf0;
`;

const StyledVerticalLine = styled.div`
  height: 100%;
  border-color: var(--gray-new-200);
`;

const StyledCoverageStatusCard = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
`;

const StyledStatusBadge = styled.span<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${({ isActive }) => (isActive ? "#dcfce7" : "#fee2e2")};
  color: ${({ isActive }) => (isActive ? "#166534" : "#991b1b")};
`;

// Format a raw pVerify MonetaryAmount string (e.g. "283") as currency ("$283.00").
const formatMonetaryAmount = (amount?: string | null) =>
  amount !== null && amount !== undefined && amount !== ""
    ? `$${parseFloat(amount).toFixed(2)}`
    : null;

// Network, coverage level and period per line — on Surgical only, as `ValueNoteSection` shows them.
const lineDetail = (tab: any, line: any) =>
  tab.detailsKey === "SurgicalSummary"
    ? {
        networkIndicator: line.networkIndicator,
        CoverageLevel: line.coverageLevel,
        timePeriodQualifier: line.timePeriodQualifier,
      }
    : {};

// The "Facility / Physician Coinsurance and Copay" tabs: one selectable row per copay / co-insurance figure of a block — its
// summary value, then each of its lines — keyed like the AI index and the engine pointer.
const summaryRow = (detailsKey: string, item: any, field: string) =>
  item?.Value !== null && item?.Value !== undefined
    ? [
        {
          name: summaryKey(detailsKey, field),
          Value: item.Value,
          Notes: item.Notes,
          subtitle: "",
        },
      ]
    : [];

const copayRows = (tab: any) => {
  const item = tab.items.find((i: any) => i.subtitle === "Copay");
  return [
    ...summaryRow(tab.detailsKey, item, "CoPayInNet"),
    ...(item?.additionalCopay ?? []).map((copay: any, index: number) => ({
      name: lineKey(tab.detailsKey, "copay", index),
      Value:
        copay.monetaryAmount != null
          ? parsePrice(Number(copay.monetaryAmount))
          : "N/A",
      subtitle: copay.tier ? `(${copay.tier})` : "",
      messages: copay.messages,
      // CLE-2816 — the engine's copay-list pick (`tagSelectedCopay`), tagged "Selected" for everyone.
      engineSelected: Boolean(item?.selectedCopayIndexes?.includes(index)),
      ...lineDetail(tab, copay),
    })),
  ];
};

// CLE-2827 — a known payer rule corrected the copay, so the field matches none of the payer's lines.
// One extra row on the block the copay came from shows the corrected amount and why.
const correctedCopayRow = (response: any, detailsKey: string) => {
  const engine = response?.engineBenefitSelections?.benefits?.FLAT_COPAY;
  if (
    engine?.quoted === true &&
    engine.provenance?.rung === "OVERRIDE" &&
    typeof engine.value === "number"
  ) {
    return blockKeyForEngineSource(engine.provenance.source) === detailsKey
      ? [
          {
            name: "copay-corrected",
            Value: parsePrice(engine.value),
            Notes: engine.provenance.reason,
            subtitle: "(corrected)",
          },
        ]
      : [];
  }
  if (
    response?.flatCopayOverride?.reason &&
    typeof response.flatCopay === "number" &&
    detailsKey === response?.practiceTypeStatus?.priorityBlock
  ) {
    return [
      {
        name: "copay-corrected",
        Value: parsePrice(response.flatCopay),
        Notes: response.flatCopayOverride.reason,
        subtitle: "(corrected)",
      },
    ];
  }
  return [];
};

const coinsuranceRows = (tab: any) => {
  const item = tab.items.find((i: any) => i.subtitle === "Coinsurance");
  return [
    ...summaryRow(tab.detailsKey, item, "CoInsInNet"),
    ...(item?.additionalCoinsurance ?? []).map(
      (coinsurance: any, index: number) => ({
        name: lineKey(tab.detailsKey, "coinsurance", index),
        // Line percents can be fractions (".2" is 20%) — the same conversion the row displayed with.
        Value:
          coinsurance.percent != null
            ? convertToPercent(String(coinsurance.percent))
            : "N/A",
        subtitle: coinsurance.tier ? `(${coinsurance.tier})` : "",
        messages: coinsurance.messages,
        benefitEntities: coinsurance.benefitEntities,
        isCoinsurance: true,
        ...lineDetail(tab, coinsurance),
      })
    ),
  ];
};

// A line stays when the filter is empty, it is the selected one, or its text contains the filter.
const filterLines = (rows: any[], filter: string, selected: string | null) =>
  rows.filter(
    (row) =>
      !filter ||
      row.name === selected ||
      [row.subtitle, row.Value, row.Notes, ...(row.messages ?? [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(filter)
  );

// The row showing the form's figure; the engine's own row wins when several show it.
const matchingRowName = (
  rows: any[],
  field: number | undefined,
  preferredName: string | null
): string | null => {
  if (field === null || field === undefined) return null;
  const matches = rows.filter(
    (row) =>
      parseFloat(String(row.Value).replace(/[^0-9.]/g, "")) === Number(field)
  );
  return (
    (matches.find((row) => row.name === preferredName) ?? matches[0])?.name ??
    null
  );
};

const enginePointerKey = (response: any, benefit: string): string | null => {
  const selection = response?.engineBenefitSelections?.benefits?.[benefit];
  return selection?.quoted === true && selection.pointer
    ? pointerKey(selection.pointer)
    : null;
};

export function PatientBenefits({
  benefitDetailsField,
  eligibilityMessage,
  deductibleField,
  outOfPocketMaxField,
  coinsuranceField,
  copayField,
  handleChangeDeductibleField,
  handleChangeOOPField,
  handleChangeCoinsuranceField,
  handleChangeCopayField,
  readOnly = false,
  fullWidth = false,
  payerType,
}: {
  benefitDetailsField: any;
  eligibilityMessage?: string | null;
  deductibleField?: number;
  outOfPocketMaxField?: number;
  coinsuranceField?: number;
  copayField?: number;
  handleChangeDeductibleField?: (value: number) => void;
  handleChangeOOPField?: (value: number) => void;
  handleChangeCoinsuranceField?: (value: number) => void;
  handleChangeCopayField?: (value: number) => void;
  readOnly?: boolean;
  fullWidth?: boolean;
  payerType?: string | null;
}) {
  // Prefer the explicitly passed payerType (CreateOrder flow); fall back to the
  // payerType stored on the benefits response so the saved OrderView dialog,
  // which does not pass the prop, still renders the Medicaid view (CLE-2466).
  const resolvedPayerType =
    payerType ?? benefitDetailsField?.response?.payerType;
  const isMedicaid = resolvedPayerType === payerTypeConstants.MEDICAID;
  console.log("resolvedPayerType", resolvedPayerType);
  const [selectedDeductible, setSelectedDeductible] = useState<string | null>(
    null
  );
  const [selectedOOP, setSelectedOOP] = useState<string | null>(null);
  const [selectedCoinsurance, setSelectedCoinsurance] = useState<string | null>(
    null
  );
  const [selectedCopay, setSelectedCopay] = useState<string | null>(null);
  // Search text of each block with many lines, keyed by `detailsKey`.
  const [lineFilters, setLineFilters] = useState<Record<string, string>>({});
  const eligibilityDetailsField = useMemo(() => {
    return benefitDetailsField?.response?.details;
  }, [benefitDetailsField]);

  // CLE-2820 — the "AI Recommendation" label, SuperAdmin only. The API now omits `aiRecommendations`
  // for anyone else as well, so this is the second of two checks rather than the only one: an order
  // saved by hospital staff has no such field stored at all. No field (AI off, skipped, too slow, or
  // the check was run by staff) means no label.
  const userAccount = useOptionalUserWithAccount();
  const isSuperAdmin = userAccount?.userInfo?.scope === UserRole.SUPER_ADMIN;
  const aiTags = useMemo(
    () =>
      isSuperAdmin
        ? buildAiTagIndex(benefitDetailsField?.response?.aiRecommendations)
        : new Set<string>(),
    [isSuperAdmin, benefitDetailsField]
  );

  // CLE-2816 — the copay row the engine quoted off a payer's per-procedure copay list, tagged
  // "Selected" for everyone. Null unless the engine's value is the one quoted (see the helper).
  const engineSelectedCopay = useMemo(
    () => selectedCopayRow(benefitDetailsField?.response),
    [benefitDetailsField]
  );

  const serviceDetails = useMemo(() => {
    const mappedServiceDetails = benefitDetailsField?.response?.serviceDetails
      ?.filter((s: any) => s.ServiceName !== "Health Benefit Plan Coverage")
      .map((s: any) => ({
        ...s,
        items: [
          {
            dates: s.EligibilityDetails?.[0]?.Date,
          },
        ],
      }));
    const filteredServiceDetails = mappedServiceDetails?.filter((s: any) =>
      s.items?.some((item: any) => item.dates?.length > 0)
    );
    return filteredServiceDetails || [];
  }, [benefitDetailsField]);

  console.log("servicedetails", serviceDetails);

  const coverageDetails = useMemo(() => {
    const coverageDetails =
      benefitDetailsField?.response?.serviceDetails?.filter(
        (s: any) => s.ServiceName === "Health Benefit Plan Coverage"
      )?.[0]?.EligibilityDetails;
    if (!coverageDetails) {
      return null;
    }
    const activeAndPrimaryCoverages = coverageDetails?.filter((c: any) => {
      return (
        c?.EligibilityOrBenefit === "Active Coverage" ||
        c?.EligibilityOrBenefit === "Primary Care Provider"
      );
    });
    return activeAndPrimaryCoverages || null;
  }, [benefitDetailsField]);

  // Check if individual deductible tier exists based on calendar year value
  // Only show N/A when calendar year is explicitly 0 (no individual tier in plan)
  // null/undefined means data not available, so check if we have remaining deductible
  const hasIndividualDeductibleTier = useMemo(() => {
    // Check remaining deductible first — a positive remaining value is direct proof the tier exists,
    // regardless of what calendar year says (some plans use Contract period, not Calendar Year)
    const remainingValue =
      benefitDetailsField?.response?.details?.HBPC_Deductible_OOP_Summary
        ?.IndividualDeductibleRemainingInNet?.Value;
    if (remainingValue !== null && remainingValue !== undefined) {
      const remainingNumeric = parseFloat(remainingValue.replace(/[$,]/g, ""));
      if (remainingNumeric > 0) {
        return true;
      }
    }
    // Remaining is absent or $0 — use calendar year to determine if tier exists
    const calendarYearValue =
      benefitDetailsField?.response?.individualCalendarYearDeductible;
    // Calendar year > 0 means tier exists (remaining $0 = deductible is met)
    if (
      calendarYearValue !== null &&
      calendarYearValue !== undefined &&
      calendarYearValue > 0
    ) {
      return true;
    }
    // Calendar year is 0 or null with no positive remaining — no individual tier
    return false;
  }, [benefitDetailsField]);

  const individualDeductible = useMemo(() => {
    // If no individual tier, show N/A
    if (!hasIndividualDeductibleTier) {
      return [{ Value: "N/A", name: "Individual Remaining 0" }];
    }

    const individualDeductibleRemainingInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualDeductibleRemainingInNet;
    const filtered = [individualDeductibleRemainingInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Individual Remaining ${index}`,
    }));
  }, [eligibilityDetailsField, hasIndividualDeductibleTier]);

  const familyDeductible = useMemo(() => {
    const familyRemainingDeductibleInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.FamilyDeductibleRemainingInNet;
    const filtered = [familyRemainingDeductibleInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Family Remaining ${index}`,
    }));
  }, [eligibilityDetailsField]);

  // For Medicaid payers we never show the Medicare Part A deductible (e.g. $1,736) that
  // pVerify can return for dual-eligible plans — only the Part B figures. We list every
  // Part B deductible line from ServiceDetails (INDIVIDUAL / FAMILY DEDUCTIBLE and their
  // REMAINING variants, all Part B), plus the plan out-of-pocket lines, as plain display
  // rows (not selectable). The "$0 because Medicaid" note lives in the benefits-form
  // warning. HBPC_Deductible_OOP_Summary is intentionally NOT read for the deductible
  // because it conflates the Part A amount for these plans (CLE-2466).
  const medicaidHbpcServiceDetails = useMemo(
    () =>
      benefitDetailsField?.response?.serviceDetails?.find(
        (s: any) => s.ServiceName === "Health Benefit Plan Coverage"
      )?.EligibilityDetails ?? [],
    [benefitDetailsField]
  );

  const medicaidDeductible = useMemo(() => {
    if (!isMedicaid) return [];
    // Every Medicare Part B "Deductible" line, in the order pVerify returned them.
    const rows = medicaidHbpcServiceDetails
      .filter(
        (d: any) =>
          d.EligibilityOrBenefit === "Deductible" &&
          d.InsuranceType === "Medicare Part B"
      )
      .map((d: any, index: number) => {
        const value = formatMonetaryAmount(d.MonetaryAmount);
        return value
          ? {
              Value: value,
              subtitle: d.PlanCoverageDescription || d.CoverageLevel || "",
              name: `Medicaid Part B Deductible ${index}`,
            }
          : null;
      })
      .filter((row: any): row is NonNullable<typeof row> => row !== null);
    console.log("rows", rows);
    if (rows.length) return rows;
    // Fall back to the explicit MedicareInfoSummary Part B figure (straight Medicare
    // dual-eligibles), otherwise a single $0 line so the section is never empty.
    const explicitPartB =
      eligibilityDetailsField?.MedicareInfoSummary?.Part_B_Deductible_Remaining
        ?.Value;
    return [
      {
        Value: explicitPartB ?? "$0.00",
        subtitle: "Individual Deductible Remaining",
        name: "Medicaid Part B Deductible",
      },
    ];
  }, [isMedicaid, eligibilityDetailsField, medicaidHbpcServiceDetails]);

  const medicaidOOP = useMemo(() => {
    if (!isMedicaid) return [];
    // Every "Out of Pocket" line (these are not split by Medicare part; exclude any
    // Part A row defensively). In the order pVerify returned them.
    const rows = medicaidHbpcServiceDetails
      .filter(
        (d: any) =>
          d.EligibilityOrBenefit === "Out of Pocket" &&
          d.InsuranceType !== "Medicare Part A"
      )
      .map((d: any, index: number) => {
        const value = formatMonetaryAmount(d.MonetaryAmount);
        return value
          ? {
              Value: value,
              subtitle: d.PlanCoverageDescription || d.CoverageLevel || "",
              name: `Medicaid Part B OOP ${index}`,
            }
          : null;
      })
      .filter((row: any): row is NonNullable<typeof row> => row !== null);
    if (rows.length) return rows;
    // Fall back to the HBPC summary remaining OOP, otherwise a single $0 line.
    const summaryOOP =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualOOPRemainingInNet?.Value;
    return [
      {
        Value: summaryOOP ?? "$0.00",
        subtitle: "Individual Out Of Pocket Remaining",
        name: "Medicaid Part B OOP",
      },
    ];
  }, [isMedicaid, eligibilityDetailsField, medicaidHbpcServiceDetails]);

  const individualOOP = useMemo(() => {
    const individualRemainingOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualOOPRemainingInNet;
    const filtered = [individualRemainingOOPInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Individual Remaining OOP ${index}`,
    }));
  }, [eligibilityDetailsField]);

  const familyOOP = useMemo(() => {
    const familyRemainingOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.FamilyOOPRemainingInNet;
    const filtered = [familyRemainingOOPInNet].filter(
      (value) => value !== null && value !== undefined
    );
    return filtered.map((value, index) => ({
      ...value,
      name: `Family Remaining OOP ${index}`,
    }));
  }, [eligibilityDetailsField]);

  // CLE-2827 — a plan total alone still shows its section, as a "not reported" row.
  const hasDeductibleTotal = useMemo(
    () =>
      (parseLimitAmount(
        benefitDetailsField?.response?.individualCalendarYearDeductible
      ) ?? 0) > 0 ||
      (parseLimitAmount(
        benefitDetailsField?.response?.familyCalendarYearDeductible
      ) ?? 0) > 0,
    [benefitDetailsField]
  );

  const hasOOPTotal = useMemo(() => {
    const summary = eligibilityDetailsField?.HBPC_Deductible_OOP_Summary;
    return [summary?.IndividualOOP_InNet, summary?.FamilyOOPInNet].some(
      (total: any) => (parseLimitAmount(total?.Value ?? total) ?? 0) > 0
    );
  }, [eligibilityDetailsField]);

  const otherOOPOptions = useMemo(() => {
    const otherOptions =
      eligibilityDetailsField?.otherRemainingOutOfPocketMaxOptions || [];
    // `originalIndex` is the option's place in the API's array — what an AI pointer names.
    return otherOptions
      .map((value: any, originalIndex: number) =>
        value !== null && value !== undefined
          ? { ...value, originalIndex }
          : value
      )
      .filter((value: any) => value !== null && value !== undefined);
  }, [eligibilityDetailsField]);

  // Separate Individual and Family OOP options
  const otherIndividualOOPOptions = useMemo(() => {
    const filtered = otherOOPOptions.filter(
      (option: any) => option.CoverageLevel === "Individual"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Individual OOP ${index}`,
    }));
  }, [otherOOPOptions]);

  const otherFamilyOOPOptions = useMemo(() => {
    const filtered = otherOOPOptions.filter(
      (option: any) => option.CoverageLevel === "Family"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Family OOP ${index}`,
    }));
  }, [otherOOPOptions]);

  const otherDeductibleOptions = useMemo(() => {
    const otherOptions = eligibilityDetailsField?.otherDeductibleOptions || [];
    // `originalIndex` is the option's place in the API's array — what an AI pointer names.
    return otherOptions
      .map((value: any, originalIndex: number) =>
        value !== null && value !== undefined
          ? { ...value, originalIndex }
          : value
      )
      .filter((value: any) => value !== null && value !== undefined);
  }, [eligibilityDetailsField]);

  // Separate Individual and Family Deductible options
  const otherIndividualDeductibleOptions = useMemo(() => {
    const filtered = otherDeductibleOptions.filter(
      (option: any) => option.CoverageLevel === "Individual"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Individual Deductible ${index}`,
    }));
  }, [otherDeductibleOptions]);

  const otherFamilyDeductibleOptions = useMemo(() => {
    const filtered = otherDeductibleOptions.filter(
      (option: any) => option.CoverageLevel === "Family"
    );
    return filtered.map((value: any, index: number) => ({
      ...value,
      name: `Other Family Deductible ${index}`,
    }));
  }, [otherDeductibleOptions]);

  // A Medicare response can report two co-insurance blocks — QMB at 0% while Medicaid
  // covers the cost-share, and standard Part B at 20% for the rest of the year — each with
  // its own plan window. The API defaults to whichever window contains the service date and
  // hands us both so the estimator can override it (CLE-2624).
  const otherCoinsuranceOptions = useMemo(() => {
    const options = eligibilityDetailsField?.otherCoinsuranceOptions || [];
    // The plan window is already carried in `Notes` ("Plan: 04/01/2026 - 12/31/2026"), which
    // BenefitLine renders under the label — do NOT also pass `dates`, or the window is printed
    // twice on every row (once left, once as the right-hand date chip).
    return options
      .filter((option: any) => option !== null && option !== undefined)
      .map((option: any, index: number) => ({
        ...option,
        subtitle: option.PolicyType,
        name: `Other Coinsurance ${index}`,
      }));
  }, [eligibilityDetailsField]);

  const specialitOfficeBenefits = useMemo(() => {
    const specialistOfficeSummaryCoPay =
      eligibilityDetailsField?.SpecialistOfficeSummary?.CoPayInNet;
    const specialistOfficeSummaryCoins =
      eligibilityDetailsField?.SpecialistOfficeSummary?.CoInsInNet;
    const individualDeductible = eligibilityDetailsField
      ?.SpecialistOfficeSummary?.IndividualDeductibleRemainingInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.SpecialistOfficeSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.SpecialistOfficeSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: specialistOfficeSummaryCoPay?.Value,
      Notes: specialistOfficeSummaryCoPay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: specialistOfficeSummaryCoins?.Value,
      Notes: specialistOfficeSummaryCoins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      specialistOfficeSummaryCoPay || copayFromServiceDetails
        ? copayDetails
        : null;
    const item2 =
      specialistOfficeSummaryCoins || coinsuranceFromServiceDetails?.length
        ? coinsDetails
        : null;
    const item3 = individualDeductible
      ? { subtitle: "Remaining Deductible", ...individualDeductible }
      : null;
    return [item1, item2, item3].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const ascSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.ASC_FacilitySummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.ASC_FacilitySummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.ASC_FacilitySummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.ASC_FacilitySummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const hospitalInpatientSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.HospitalInpatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.HospitalInpatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.HospitalInpatientSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.HospitalInpatientSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const hospitalOutpatientBenefits = useMemo(() => {
    const copay = eligibilityDetailsField?.HospitalOutPatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.HospitalOutPatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.HospitalOutPatientSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.HospitalOutPatientSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };

    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const mriCtSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.MRI_CT_ScanSummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.MRI_CT_ScanSummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.MRI_CT_ScanSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.MRI_CT_ScanSummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const surgicalSummary = useMemo(() => {
    // Only show surgical copays with the specific surgeon message
    const surgicalCopaysFromServiceDetails =
      eligibilityDetailsField?.SurgicalSummary?.serviceDetails?.copay?.filter(
        (copay: any) =>
          copay.messages &&
          copay.messages.some(
            (msg: string) =>
              msg === "COPAYMENT PER EACH PERFORMING SURGEON AND CO-SURGEON" ||
              msg ===
                "IN SETTING OTHER THAN OFFICE; COPAYMENT PER EACH PERFORMING SURGEON AND CO-SURGEON"
          )
      );

    const surgicalCopayDetails = {
      subtitle: "Surgical Copay",
      Value: null,
      Notes: null,
      additionalCopay: surgicalCopaysFromServiceDetails,
    };

    const item1 = surgicalCopaysFromServiceDetails?.length
      ? surgicalCopayDetails
      : null;

    return [item1].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  /**
   * CLE-2675 — the whole `Surgical` block (pVerify service type 2), not just the surgeon copays
   * above.
   *
   * Accounts on the by-payer benefit priority read this block for every non-Medicare-Advantage
   * payer, so it is now the section a commercial estimate's copay and coinsurance come from — and
   * until now it had no tab at all. `surgicalSummary` stays as it is: it is the narrow
   * "per performing surgeon" view, and it feeds a different question.
   *
   * Like the Specialist Office tab, this renders for any account whose payer returned the block —
   * empty tabs are filtered out below.
   */
  const surgicalBenefits = useMemo(() => {
    const copay = eligibilityDetailsField?.SurgicalSummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.SurgicalSummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.SurgicalSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.SurgicalSummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const xRaySummary = useMemo(() => {
    const copay = eligibilityDetailsField?.Diagnostic_X_Ray?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.Diagnostic_X_Ray?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    // CLE-2970 — the line rows, like the MRI/CT tab: `XRaySummary` has one copay field and hid a
    // facility copay. Orders stored before then carry no `serviceDetails` and show the summary only.
    const copaysFromServiceDetails =
      eligibilityDetailsField?.Diagnostic_X_Ray?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.Diagnostic_X_Ray?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  /**
   * CLE-3013 — pVerify's `Diagnostic Medical` block, which a Diagnostic Medical (113) check returns
   * instead of the care-setting blocks. Its copay and co-insurance are what such an estimate is
   * quoted from, so it gets a tab like the MRI/CT one, line rows included. Empty tabs are filtered
   * out below, so it renders only when the response carried the block.
   */
  const diagnosticMedicalSummary = useMemo(() => {
    const block = eligibilityDetailsField?.DiagnosticMedicalSummary;
    const copay = block?.CoPayInNet as { Value: string; Notes: string } | null;
    const coins = block?.CoInsInNet as { Value: string; Notes: string } | null;
    const copaysFromServiceDetails = block?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails = block?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const showAdditionalDetailsSection = useMemo(() => {
    const hasData =
      individualDeductible.length > 0 ||
      familyDeductible.length > 0 ||
      individualOOP.length > 0 ||
      familyOOP.length > 0 ||
      hasDeductibleTotal ||
      hasOOPTotal ||
      otherOOPOptions.length > 0 ||
      otherDeductibleOptions.length > 0 ||
      specialitOfficeBenefits.length > 0 ||
      ascSummary.length > 0 ||
      hospitalInpatientSummary.length > 0 ||
      mriCtSummary.length > 0 ||
      hospitalOutpatientBenefits.length > 0 ||
      surgicalBenefits.length > 0 ||
      surgicalSummary.length > 0 ||
      diagnosticMedicalSummary.length > 0;
    return eligibilityDetailsField && hasData;
  }, [
    individualDeductible,
    familyDeductible,
    individualOOP,
    familyOOP,
    hasDeductibleTotal,
    hasOOPTotal,
    otherOOPOptions,
    otherDeductibleOptions,
    eligibilityDetailsField,
    specialitOfficeBenefits,
    ascSummary,
    hospitalInpatientSummary,
    mriCtSummary,
    hospitalOutpatientBenefits,
    surgicalBenefits,
    surgicalSummary,
    diagnosticMedicalSummary,
  ]);

  const serviceCoverage = useMemo(() => {
    return benefitDetailsField?.response?.serviceCoverage || {};
  }, [benefitDetailsField]);

  // Basic plan coverage info for fallback display when no detailed benefits
  const planCoverageInfo = useMemo(() => {
    const summary = benefitDetailsField?.response?.planCoverageSummary;
    if (!summary?.Status) return null;
    return {
      status: summary.Status,
      planName: summary.PlanName || null,
      effectiveDate: summary.EffectiveDate || null,
      policyType: summary.PolicyType || null,
    };
  }, [benefitDetailsField]);

  const tabs = useMemo(() => {
    // CLE-2933 — the payer's qualifier under a tab's NON COVERED status, from the block's in-network
    // Non-Covered lines. Read only from the grouped field: a response stored before it existed keeps
    // the plain footer until it is re-run.
    const qualifiersFor = (detailsKey: string) =>
      nonCoveredQualifiers(
        eligibilityDetailsField?.[detailsKey]?.serviceDetails?.nonCovered
      );
    return [
      {
        title: "Hospital Outpatient",
        detailsKey: "HospitalOutPatientSummary",
        items: hospitalOutpatientBenefits,
        serviceCoverage: serviceCoverage?.hospitalOutpatient,
        nonCoveredQualifiers: qualifiersFor("HospitalOutPatientSummary"),
      },
      {
        title: "ASC Facility",
        detailsKey: "ASC_FacilitySummary",
        items: ascSummary,
        serviceCoverage: serviceCoverage?.ascFacility,
        nonCoveredQualifiers: qualifiersFor("ASC_FacilitySummary"),
      },
      {
        title: "Hospital Inpatient",
        detailsKey: "HospitalInpatientSummary",
        items: hospitalInpatientSummary,
        serviceCoverage: serviceCoverage?.hospitalInpatient,
        nonCoveredQualifiers: qualifiersFor("HospitalInpatientSummary"),
      },
      // Disabled since the May 2025 redesign with no recorded reason; re-enabled for CLE-2675 —
      // office-based accounts (Vein-ity) run Specialist Office eligibility checks and could not
      // see the block their copay was selected from. Empty tabs are filtered out below, so this
      // renders only when the payer actually returned office-visit data.
      {
        title: "Specialist Office",
        detailsKey: "SpecialistOfficeSummary",
        items: specialitOfficeBenefits,
        serviceCoverage: serviceCoverage?.specialistOffice,
        nonCoveredQualifiers: qualifiersFor("SpecialistOfficeSummary"),
      },
      // CLE-2675 — pVerify's `Surgical` block (service type 2). Ani's name, chosen 2026-08-25 to
      // be the ONE word used for this benefit everywhere: the Service Type options, this tab and
      // the account's benefit-priority setting.
      //
      // There is deliberately NO tab for pVerify's `Professional (Physician) Visit - Outpatient`
      // block. It had one ("Physician Outpatient", then "Professional Visit - Outpatient") until
      // 2026-09-09, when Ani retired the block outright: the platform behaves as if pVerify never
      // sent it, and the API no longer summarises it, so nothing here could render it anyway.
      {
        title: "Surgical (Physician Benefit)",
        detailsKey: "SurgicalSummary",
        items: surgicalBenefits,
      },
      {
        title: "MRI/CT Scan Benefits",
        detailsKey: "MRI_CT_ScanSummary",
        items: mriCtSummary,
        serviceCoverage: serviceCoverage?.mriCt,
        nonCoveredQualifiers: qualifiersFor("MRI_CT_ScanSummary"),
      },
      {
        title: "Diagnostic X-Ray",
        detailsKey: "Diagnostic_X_Ray",
        items: xRaySummary,
        nonCoveredQualifiers: qualifiersFor("Diagnostic_X_Ray"),
      },
      // CLE-3013 — keyed by the summary field, like every tab a value is selected from, so an
      // engine or AI pointer naming `DiagnosticMedicalSummary` tags its row here.
      {
        title: "Diagnostic Medical",
        detailsKey: "DiagnosticMedicalSummary",
        items: diagnosticMedicalSummary,
        serviceCoverage: serviceCoverage?.diagnosticMedical,
        nonCoveredQualifiers: qualifiersFor("DiagnosticMedicalSummary"),
      },
    ].filter((tab) => tab.items.length > 0);
  }, [
    hospitalOutpatientBenefits,
    ascSummary,
    hospitalInpatientSummary,
    specialitOfficeBenefits,
    surgicalBenefits,
    mriCtSummary,
    xRaySummary,
    diagnosticMedicalSummary,
    serviceCoverage,
    eligibilityDetailsField,
  ]);

  // The tabs the API's `benefitDisplayBlocks` names, in its order; for older responses the blocks
  // `visibleBenefitBlocks` derives plus the two physician blocks, in `BENEFIT_BLOCK_ORDER`.
  const visibleTabs = useMemo(() => {
    const blocks = visibleBenefitBlocks(benefitDetailsField?.response);
    const fromApi = apiDisplayBlocks(benefitDetailsField?.response) !== null;
    const sources = sourceBenefitBlocks(benefitDetailsField?.response);
    return tabs
      .filter(
        (tab) =>
          blocks === "ALL" ||
          blocks.includes(tab.detailsKey) ||
          (!fromApi && PHYSICIAN_BENEFIT_BLOCKS.has(tab.detailsKey))
      )
      .sort((a, b) =>
        fromApi
          ? blocks.indexOf(a.detailsKey) - blocks.indexOf(b.detailsKey)
          : BENEFIT_BLOCK_ORDER.indexOf(a.detailsKey) -
            BENEFIT_BLOCK_ORDER.indexOf(b.detailsKey)
      )
      .map((tab) => ({
        ...tab,
        // Pre-selection reads only the blocks a value came from, never the API list (Mike, 2026-10-31).
        isSourceBlock: sources === "ALL" || sources.includes(tab.detailsKey),
        copayRows: [
          ...correctedCopayRow(benefitDetailsField?.response, tab.detailsKey),
          ...copayRows({
            ...tab,
            items: tagSelectedCopay(
              tab.items,
              tab.detailsKey,
              engineSelectedCopay
            ),
          }),
        ].map((row) => ({
          ...row,
          aiRecommended: aiTags.has(row.name),
        })),
        coinsuranceRows: coinsuranceRows(tab).map((row) => ({
          ...row,
          aiRecommended: aiTags.has(row.name),
        })),
        otherItems: tab.items.filter(
          (item: any) =>
            item.subtitle !== "Copay" && item.subtitle !== "Coinsurance"
        ),
      }));
  }, [tabs, benefitDetailsField, aiTags, engineSelectedCopay]);

  useEffect(() => {
    const deductibleValues = [
      ...(individualDeductible.length ? [individualDeductible[0]] : []),
      ...(familyDeductible.length ? [familyDeductible[0]] : []),
      ...otherIndividualDeductibleOptions,
      ...otherFamilyDeductibleOptions,
    ];
    const formattedDeductible = Number(deductibleField).toFixed(2);
    const matchingField = deductibleValues.find(
      (value) => value.Value === `$${formattedDeductible}`
    );
    if (matchingField) {
      setSelectedDeductible(matchingField.name);
    } else {
      setSelectedDeductible(null);
    }
  }, [
    individualDeductible,
    familyDeductible,
    otherIndividualDeductibleOptions,
    otherFamilyDeductibleOptions,
    deductibleField,
    benefitDetailsField,
  ]);

  useEffect(() => {
    const oopValues = [
      ...individualOOP,
      ...familyOOP,
      ...otherIndividualOOPOptions,
      ...otherFamilyOOPOptions,
    ];
    const formattedOOP =
      outOfPocketMaxField === null || outOfPocketMaxField === undefined
        ? null
        : Number(outOfPocketMaxField).toFixed(2);
    const matchingField = oopValues.find(
      (value) => formattedOOP !== null && value.Value === `$${formattedOOP}`
    );
    if (matchingField) {
      setSelectedOOP(matchingField.name);
    } else {
      setSelectedOOP(null);
    }
  }, [
    outOfPocketMaxField,
    individualOOP,
    familyOOP,
    otherIndividualOOPOptions,
    otherFamilyOOPOptions,
  ]);

  useEffect(() => {
    const matchingField = otherCoinsuranceOptions.find(
      (option: any) => option.Percent === Number(coinsuranceField)
    );
    setSelectedCoinsurance(
      matchingField
        ? matchingField.name
        : matchingRowName(
            visibleTabs
              .filter((tab) => tab.isSourceBlock)
              .flatMap((tab) => tab.coinsuranceRows),
            coinsuranceField,
            enginePointerKey(
              benefitDetailsField?.response,
              "COINSURANCE_PERCENT"
            )
          )
    );
  }, [
    coinsuranceField,
    otherCoinsuranceOptions,
    visibleTabs,
    benefitDetailsField,
  ]);

  const surgicalCopayIsFlatCopay =
    flatCopaySourceBlock(benefitDetailsField?.response) === "SurgicalSummary";

  // Surgical copay rows are the physician copay, a different field, so they are never matched here — unless the
  // response says the flat copay was read from Surgical, as on payer-routed accounts.
  useEffect(() => {
    setSelectedCopay(
      matchingRowName(
        visibleTabs
          .filter((tab) => tab.isSourceBlock)
          .filter(
            (tab) =>
              tab.detailsKey !== "SurgicalSummary" || surgicalCopayIsFlatCopay
          )
          .flatMap((tab) => tab.copayRows),
        copayField,
        enginePointerKey(benefitDetailsField?.response, "FLAT_COPAY")
      )
    );
  }, [copayField, visibleTabs, benefitDetailsField, surgicalCopayIsFlatCopay]);

  const handleSelectDeductible = (value: string, name: string) => {
    setSelectedDeductible(name);
    const valueFromString = value.split("$")[1];
    const valueFromNumber = parseFloat(valueFromString);
    handleChangeDeductibleField?.(valueFromNumber);
  };

  const handleSelectOOP = (value: string, name: string) => {
    setSelectedOOP(name);
    const valueFromString = value.split("$")[1];
    const valueFromNumber = parseFloat(valueFromString);
    handleChangeOOPField?.(valueFromNumber);
  };

  const handleSelectCoinsurance = (value: string, name: string) => {
    setSelectedCoinsurance(name);
    // Co-insurance options are percentages ("20%"), not the "$0.00" the other two carry.
    const valueFromNumber = parseFloat(value.replace("%", ""));
    if (!Number.isNaN(valueFromNumber)) {
      handleChangeCoinsuranceField?.(valueFromNumber);
    }
  };

  const handleSelectCopay = (value: string, name: string) => {
    setSelectedCopay(name);
    // `parsePrice` writes thousands separators ("$1,000.00"), so strip everything but the number.
    const valueFromNumber = parseFloat(String(value).replace(/[^0-9.]/g, ""));
    if (!Number.isNaN(valueFromNumber)) {
      handleChangeCopayField?.(valueFromNumber);
    }
  };

  const showBasicCoverageOnly =
    !showAdditionalDetailsSection && planCoverageInfo;

  return (
    <div
      className={`flex flex-row w-full min-w-0 ${
        fullWidth ? "" : "xl:w-8"
      } align-items-start`}
    >
      {/* min-w-0: a flex item never shrinks below its content's width, and the tab strip is nowrap, so
          without it the panel grows past the card (same for StyledBenefitCointainer and StyledTabView). */}
      {!fullWidth &&
        (eligibilityMessage ||
          showAdditionalDetailsSection ||
          showBasicCoverageOnly) && (
          <StyledVerticalLine className="hidden xl:block border-left-1 ml-4 mr-4" />
        )}
      <StyledBenefitCointainer
        className="w-full pt-5 mb-4"
        showAdditionalDetailsSection={
          showAdditionalDetailsSection || showBasicCoverageOnly
        }
        $fullWidth={fullWidth}
      >
        {!eligibilityMessage &&
          !showAdditionalDetailsSection &&
          !planCoverageInfo && (
            <div className="flex w-full flex-column align-items-center">
              <Empty width={100} height={100} style={{ color: "#CED4DA" }} />
              <p style={{ color: "#ced4da" }}>
                Please, run eligibility to get information
              </p>
            </div>
          )}
        {showBasicCoverageOnly && (
          <StyledCoverageStatusCard>
            <div className="flex flex-column gap-4">
              <div className="flex align-items-center justify-content-between">
                <span className="text-lg font-semibold text-gray-700">
                  Coverage Status
                </span>
                <StyledStatusBadge
                  isActive={planCoverageInfo.status === "Active"}
                >
                  {planCoverageInfo.status === "Active" ? (
                    <i className="pi pi-check-circle" />
                  ) : (
                    <i className="pi pi-times-circle" />
                  )}
                  {planCoverageInfo.status}
                </StyledStatusBadge>
              </div>
              {planCoverageInfo.planName && (
                <div className="flex flex-column gap-1">
                  <span className="text-sm text-gray-500">Plan Name</span>
                  <span className="text-base font-medium text-gray-800">
                    {planCoverageInfo.planName}
                  </span>
                </div>
              )}
              {planCoverageInfo.effectiveDate && (
                <div className="flex flex-column gap-1">
                  <span className="text-sm text-gray-500">Effective Date</span>
                  <span className="text-base text-gray-700">
                    {planCoverageInfo.effectiveDate}
                  </span>
                </div>
              )}
              {planCoverageInfo.policyType && (
                <div className="flex flex-column gap-1">
                  <span className="text-sm text-gray-500">Policy Type</span>
                  <span className="text-base text-gray-700">
                    {planCoverageInfo.policyType}
                  </span>
                </div>
              )}
              <p className="text-sm text-gray-500 mt-2">
                Detailed benefit information is not available for this plan.
                Please contact the payer for specific coverage details.
              </p>
            </div>
          </StyledCoverageStatusCard>
        )}
        {eligibilityMessage && (
          <Message
            eligibilityMessage={eligibilityMessage}
            showAdditionalDetailsSection={showAdditionalDetailsSection}
          />
        )}
        {showAdditionalDetailsSection && (
          <div className="w-full flex overflow-x-auto">
            <StyledTabView>
              <TabPanel header="Health Plan Benefit Coverage">
                <StyledTabPanelheader className="flex justify-content-between header">
                  <p className="text-xs">Description</p>
                  <p className="text-xs">Benefit Details</p>
                </StyledTabPanelheader>
                {isMedicaid && coverageDetails?.length && (
                  <div className="mt-4">
                    <p className="px-4 mb-3 text-lg font-bold">
                      Health Benefit Plan Coverage Details
                    </p>
                    {coverageDetails.map((d: any, i: number) => (
                      <CoverageDetailsLine
                        key={i}
                        title={`${d.CoverageLevel} ${d.EligibilityOrBenefit}`}
                        planCoverageDescription={d.PlanCoverageDescription}
                        date={d?.Date?.length && d?.Date[0]}
                        benefitEntities={d?.BenefitEntities}
                        insuranceType={d?.InsuranceType}
                      />
                    ))}
                  </div>
                )}
                {isMedicaid && serviceDetails?.length ? (
                  <div className="mt-4">
                    <p className="px-4 mb-3 text-lg font-bold">Services</p>
                    {serviceDetails.map((s: any, i: number) => (
                      <ValueNoteSection
                        key={i}
                        title={s.ServiceName}
                        items={s.items}
                        alignWithRadio
                      />
                    ))}
                  </div>
                ) : null}

                {/* Medicaid: show only Medicare Part B (or $0) with a zeroed-out note */}
                {isMedicaid && (
                  <>
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">Deductible</p>
                      <ValueNoteSection
                        title="Medicare Part B"
                        items={medicaidDeductible}
                      />
                    </div>
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">
                        Out of Pocket Max
                      </p>
                      <ValueNoteSection
                        title="Medicare Part B"
                        items={medicaidOOP}
                      />
                    </div>
                  </>
                )}

                {/* Deductible Section */}
                {!isMedicaid &&
                  (individualDeductible.length > 0 ||
                    familyDeductible.length > 0 ||
                    hasDeductibleTotal ||
                    otherIndividualDeductibleOptions.length > 0 ||
                    otherFamilyDeductibleOptions.length > 0) && (
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">Deductible</p>
                      <ValueNoteSection
                        title="Individual Remaining"
                        items={
                          individualDeductible.length
                            ? withLimit(
                                tagSummaryItems(
                                  individualDeductible,
                                  "IndividualDeductibleRemainingInNet",
                                  aiTags
                                ),
                                benefitDetailsField?.response
                                  ?.individualCalendarYearDeductible
                              )
                            : missingRow(
                                "Individual Remaining missing",
                                benefitDetailsField?.response
                                  ?.individualCalendarYearDeductible
                              )
                        }
                        selectable={
                          hasIndividualDeductibleTier &&
                          !readOnly &&
                          individualDeductible.length > 0
                        }
                        markSelected={readOnly}
                        onSelect={
                          hasIndividualDeductibleTier
                            ? handleSelectDeductible
                            : undefined
                        }
                        selected={selectedDeductible}
                        alignWithRadio={
                          !readOnly &&
                          (!hasIndividualDeductibleTier ||
                            individualDeductible.length === 0)
                        }
                      />
                      <ValueNoteSection
                        title="Family Remaining"
                        items={
                          familyDeductible.length
                            ? withLimit(
                                tagSummaryItems(
                                  familyDeductible,
                                  "FamilyDeductibleRemainingInNet",
                                  aiTags
                                ),
                                benefitDetailsField?.response
                                  ?.familyCalendarYearDeductible
                              )
                            : missingRow(
                                "Family Remaining missing",
                                benefitDetailsField?.response
                                  ?.familyCalendarYearDeductible
                              )
                        }
                        selectable={!readOnly && familyDeductible.length > 0}
                        markSelected={readOnly}
                        onSelect={handleSelectDeductible}
                        selected={selectedDeductible}
                        alignWithRadio={
                          !readOnly && familyDeductible.length === 0
                        }
                      />
                      {otherIndividualDeductibleOptions.length > 0 && (
                        <ValueNoteSection
                          title="Individual Remaining"
                          items={withLimit(
                            tagOptionItems(
                              otherIndividualDeductibleOptions,
                              "otherDeductibleOptions",
                              aiTags
                            ),
                            benefitDetailsField?.response
                              ?.individualCalendarYearDeductible
                          )}
                          selectable={!readOnly}
                          markSelected={readOnly}
                          onSelect={handleSelectDeductible}
                          selected={selectedDeductible}
                        />
                      )}
                      {otherFamilyDeductibleOptions.length > 0 && (
                        <ValueNoteSection
                          title="Family Remaining"
                          items={withLimit(
                            tagOptionItems(
                              otherFamilyDeductibleOptions,
                              "otherDeductibleOptions",
                              aiTags
                            ),
                            benefitDetailsField?.response
                              ?.familyCalendarYearDeductible
                          )}
                          selectable={!readOnly}
                          markSelected={readOnly}
                          onSelect={handleSelectDeductible}
                          selected={selectedDeductible}
                        />
                      )}
                    </div>
                  )}

                {/* Out of Pocket Max Section */}
                {!isMedicaid &&
                  (individualOOP.length > 0 ||
                    familyOOP.length > 0 ||
                    hasOOPTotal ||
                    otherIndividualOOPOptions.length > 0 ||
                    otherFamilyOOPOptions.length > 0) && (
                    <div className="mt-4">
                      <p className="px-4 mb-3 text-lg font-bold">
                        Out of Pocket Max
                      </p>
                      {/* CLE-2684 — the plan totals are now limits under the remaining rows.
                          pVerify names the individual total IndividualOOP_InNet (underscore)
                          but the family one FamilyOOPInNet (none). */}
                      <ValueNoteSection
                        title="Individual Remaining"
                        items={
                          individualOOP.length
                            ? withLimit(
                                tagSummaryItems(
                                  individualOOP,
                                  "IndividualOOPRemainingInNet",
                                  aiTags
                                ),
                                eligibilityDetailsField
                                  ?.HBPC_Deductible_OOP_Summary
                                  ?.IndividualOOP_InNet
                              )
                            : missingRow(
                                "Individual Remaining OOP missing",
                                eligibilityDetailsField
                                  ?.HBPC_Deductible_OOP_Summary
                                  ?.IndividualOOP_InNet
                              )
                        }
                        selectable={!readOnly && individualOOP.length > 0}
                        markSelected={readOnly}
                        onSelect={handleSelectOOP}
                        selected={selectedOOP}
                        alignWithRadio={!readOnly && individualOOP.length === 0}
                      />
                      {otherIndividualOOPOptions.length > 0 && (
                        <ValueNoteSection
                          title="Individual Remaining"
                          items={withLimit(
                            tagOptionItems(
                              otherIndividualOOPOptions,
                              "otherRemainingOutOfPocketMaxOptions",
                              aiTags
                            ),
                            eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
                              ?.IndividualOOP_InNet
                          )}
                          selectable={!readOnly}
                          markSelected={readOnly}
                          onSelect={handleSelectOOP}
                          selected={selectedOOP}
                        />
                      )}
                      <ValueNoteSection
                        title="Family Remaining"
                        items={
                          familyOOP.length
                            ? withLimit(
                                tagSummaryItems(
                                  familyOOP,
                                  "FamilyOOPRemainingInNet",
                                  aiTags
                                ),
                                eligibilityDetailsField
                                  ?.HBPC_Deductible_OOP_Summary?.FamilyOOPInNet
                              )
                            : missingRow(
                                "Family Remaining OOP missing",
                                eligibilityDetailsField
                                  ?.HBPC_Deductible_OOP_Summary?.FamilyOOPInNet
                              )
                        }
                        selectable={!readOnly && familyOOP.length > 0}
                        markSelected={readOnly}
                        onSelect={handleSelectOOP}
                        selected={selectedOOP}
                        alignWithRadio={!readOnly && familyOOP.length === 0}
                      />
                      {otherFamilyOOPOptions.length > 0 && (
                        <ValueNoteSection
                          title="Family Remaining"
                          items={withLimit(
                            tagOptionItems(
                              otherFamilyOOPOptions,
                              "otherRemainingOutOfPocketMaxOptions",
                              aiTags
                            ),
                            eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
                              ?.FamilyOOPInNet
                          )}
                          selectable={!readOnly}
                          markSelected={readOnly}
                          onSelect={handleSelectOOP}
                          selected={selectedOOP}
                        />
                      )}
                    </div>
                  )}

                {/* Coinsurance Section — only rendered when the payer reported more
                    than one co-insurance block to choose between (CLE-2624) */}
                {otherCoinsuranceOptions.length > 0 && (
                  <div className="mt-4">
                    <p className="px-4 mb-3 text-lg font-bold">Coinsurance</p>
                    <ValueNoteSection
                      title="Coinsurance"
                      items={otherCoinsuranceOptions}
                      selectable={!readOnly}
                      markSelected={readOnly}
                      onSelect={handleSelectCoinsurance}
                      selected={selectedCoinsurance}
                    />
                  </div>
                )}
              </TabPanel>
              {visibleTabs.map((tab) => {
                // The block's coverage footer goes under its last section that has rows (an
                // empty section renders nothing, footer included).
                const coverage = {
                  serviceCoverage: tab.serviceCoverage,
                  nonCoveredQualifiers: tab.nonCoveredQualifiers,
                };
                // A block with many lines (a UHC plan sends 227) gets a search box; every block's lines
                // scroll inside a bounded box, so a tab never outgrows the card.
                const searchable =
                  tab.copayRows.length + tab.coinsuranceRows.length > 8;
                const filter = searchable
                  ? (lineFilters[tab.detailsKey] ?? "").trim().toLowerCase()
                  : "";
                const shownCopayRows = filterLines(
                  tab.copayRows,
                  filter,
                  selectedCopay
                );
                const shownCoinsuranceRows = filterLines(
                  tab.coinsuranceRows,
                  filter,
                  selectedCoinsurance
                );
                const lineSections = (
                  <>
                    <ValueNoteSection
                      title="Copay"
                      items={shownCopayRows}
                      selectable={
                        !readOnly &&
                        (tab.detailsKey !== "SurgicalSummary" ||
                          surgicalCopayIsFlatCopay)
                      }
                      markSelected={readOnly}
                      onSelect={handleSelectCopay}
                      selected={selectedCopay}
                      alignWithRadio={!readOnly}
                      {...(!shownCoinsuranceRows.length &&
                        !tab.otherItems.length &&
                        coverage)}
                    />
                    <ValueNoteSection
                      title="Coinsurance"
                      items={shownCoinsuranceRows}
                      selectable={!readOnly}
                      markSelected={readOnly}
                      onSelect={handleSelectCoinsurance}
                      selected={selectedCoinsurance}
                      alignWithRadio={!readOnly}
                      {...(!tab.otherItems.length && coverage)}
                    />
                    {filter &&
                      !shownCopayRows.length &&
                      !shownCoinsuranceRows.length && (
                        <p
                          className="px-4 text-sm"
                          style={{ color: "var(--brand-gray-600)" }}
                        >
                          No lines match &quot;{filter}&quot;
                        </p>
                      )}
                  </>
                );
                return (
                  <TabPanel key={tab.detailsKey} header={tab.title}>
                    {searchable && (
                      <div className="px-4 mb-2">
                        <InputText
                          value={lineFilters[tab.detailsKey] ?? ""}
                          onChange={(e) =>
                            setLineFilters((prev) => ({
                              ...prev,
                              [tab.detailsKey]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.preventDefault();
                          }}
                          placeholder="Search copay / coinsurance lines"
                          className="p-inputtext-sm w-full"
                          data-testid={`lineSearch-${tab.detailsKey}`}
                        />
                      </div>
                    )}
                    <div
                      style={{
                        maxHeight: "28rem",
                        overflowY: "auto",
                      }}
                    >
                      {lineSections}
                    </div>
                    <ValueNoteSection
                      title={tab.title}
                      items={tagSelectedCopay(
                        tab.otherItems,
                        tab.detailsKey,
                        engineSelectedCopay
                      )}
                      {...coverage}
                    />
                  </TabPanel>
                );
              })}
            </StyledTabView>
          </div>
        )}
      </StyledBenefitCointainer>
    </div>
  );
}
